import React from 'react';
import { CaseStudyShell } from '../components/case-study/CaseStudyShell';
import { CaseHeader } from '../components/case-study/CaseHeader';
import { CaseSection } from '../components/case-study/CaseSection';
import { ImageFigure } from '../components/case-study/ImageFigure';
import { NextCase } from '../components/case-study/NextCase';
import { Reveal } from '../components/Reveal';
import { ProcessSteps } from '../components/palacio/ProcessSteps';
import { StorePhotos } from '../components/palacio/StorePhotos';
import { FieldPolaroids } from '../components/palacio/FieldPolaroids';
import { palacioDecisions, palacioDelivery, palacioDiscovery, palacioMeta, palacioProblem, palacioQuotes, palacioStats } from '../data/palacio';
import { palacioImages } from '../data/palacioImages';
export function PalacioCaseStudy() {
  return <CaseStudyShell data-id="element-912" data-mp="28x1xcwaqbqp8">
      <CaseHeader overline="Case study · Luxury retail & fintech" title="Palacio de Hierro: luxury shopping and credit in one app" intro="Design, build and launch the first “Super App” that unifies luxury e-commerce and Palacio de Hierro credit-card management in under 12 months. I led the credit workstream and two junior designers." meta={palacioMeta} data-id="element-913" data-mp="24tc9ya5sjf5s" />

      <ImageFigure className="mt-14" {...palacioImages.heroScreens} caption="The shipped credit flows: login, card details, statement and payments." data-id="element-914" data-mp="3oy9gbpxpq9w6" />

      <CaseSection index="01" label="The problem" title="Two products, one customer, no shared home" data-id="element-915" data-mp="ggd90ouen19c">
        <Reveal data-id="element-916" data-mp="lf6qesctbces">
          <p className="rounded-3xl bg-night px-6 py-10 text-2xl font-semibold leading-snug tracking-tight text-white md:px-10 md:py-14 md:text-[34px]" data-id="element-917" data-mp="7hrtz3o564db">
            {palacioProblem.lead}
            <span className="text-coral" data-id="element-918" data-mp="bl7o9tcqnsmu">{palacioProblem.highlight}</span>
            {palacioProblem.tail}
          </p>
        </Reveal>
        <p className="mt-6 max-w-2xl text-[15px] leading-relaxed text-fg-muted" data-id="element-919" data-mp="2w9ud00ny1g0o">
          There was no budget or time for two separate apps, so retail and credit had to live together, with a super app
          as the reference model. The harder problem was people: around 40 stakeholders, and marketing and credit both
          wanted the same home screen.
        </p>
      </CaseSection>

      <CaseSection index="02" label="Process" title="Alignment first, pixels second" data-id="element-920" data-mp="o2igq7io7qnd">
        <ProcessSteps data-id="element-921" data-mp="k043c2xrtj3h" />
      </CaseSection>

      <CaseSection index="03" label="Discovery" title="Discovery highlights" intro={<ul className="list-disc space-y-2 pl-5" data-id="element-923" data-mp="f3dlba2eodff">
            {palacioDiscovery.map(item => <li key={item} data-id="element-924" data-mp="a4ag9pmfe77g">{item}</li>)}
          </ul>} data-id="element-922" data-mp="k1ynvg54r971">
        <StorePhotos data-id="element-925" data-mp="54xtf4vk104v" />
        <p className="mt-3 text-xs leading-relaxed text-fg-faint" data-id="element-926" data-mp="55he2tcla2ml">
          In-store immersion: the credit kiosks were the only place cardholders could check their account.
        </p>
        <div className="mt-10 grid items-start gap-6 md:grid-cols-2" data-id="element-927" data-mp="1zf1hbreok1w5">
          <ImageFigure {...palacioImages.benchmark} caption="Benchmark: Liverpool and Mercado Libre as competitors, Chanel, Louis Vuitton, Amex and Rappi as the bar." data-id="element-928" data-mp="27xd6epej4b7c" />
          <ImageFigure {...palacioImages.personas} caption="The four personas: the Fashionista, the Icon, the Ripper and the Family." data-id="element-929" data-mp="2grrjclbl79na" />
        </div>
      </CaseSection>

      <CaseSection index="04" label="Delivery" title="Two workstreams, one weekly rhythm" intro={<ul className="list-disc space-y-2 pl-5" data-id="element-931" data-mp="1t1e1toqmot6y">
            {palacioDelivery.map(item => <li key={item} data-id="element-932" data-mp="1xydwz4lxwbja">{item}</li>)}
          </ul>} data-id="element-930" data-mp="1ozsflcs7f2k3">
        <ImageFigure {...palacioImages.wireframes} caption="Annotated wireframes with stakeholder comments, before design skinning." data-id="element-933" data-mp="23036ugxfej5s" />
      </CaseSection>

      <CaseSection index="05" label="Key decisions" title="Three calls that shaped the app" data-id="element-934" data-mp="3edfi3cvim41z">
        <ol className="space-y-3" data-id="element-935" data-mp="r59eam5bfdqn">
          {palacioDecisions.map((decision, i) => <Reveal key={decision.letter} delay={i * 0.05} data-id="element-936" data-mp="m6y4gbf0t38f" data-mpi={i}>
              <li className="grid grid-cols-[32px_1fr] gap-4 rounded-2xl bg-surface p-5 md:grid-cols-[56px_1fr] md:p-6" data-id="element-937" data-mp="m8j1q8spz09z">
                <span className="text-xl font-semibold text-rust" data-id="element-938" data-mp="v7crbf7muqe1">{decision.letter}</span>
                <div data-id="element-939" data-mp="v9pjel73uh4t">
                  <h3 className="text-[15px] font-semibold text-fg" data-id="element-940" data-mp="1a4dldpdaqxfo">{decision.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-fg-muted" data-id="element-941" data-mp="155k4ro2pqqba">{decision.body}</p>
                </div>
              </li>
            </Reveal>)}
        </ol>
      </CaseSection>

      <CaseSection index="06" label="Results" title="Launched a month early, and people liked it" data-id="element-942" data-mp="1evc0wmqgnrhv">
        <Reveal data-id="element-943" data-mp="1sv4ssldq4drf">
          <dl className="grid gap-y-8 sm:grid-cols-2 md:grid-cols-4 md:divide-x md:divide-line" data-id="element-944" data-mp="1suljft252xp0">
            {palacioStats.map(stat => <div key={stat.value} className="md:px-5 md:first:pl-0" data-id="element-945" data-mp="1nxv457y5s64n">
                <dt className="sr-only" data-id="element-946" data-mp="27ivb5dv8ghq3">{stat.label}</dt>
                <dd data-id="element-947" data-mp="2cgwtfwku4d0i">
                  <span className="block text-3xl font-semibold tracking-tight text-ink md:text-[34px]" data-id="element-948" data-mp="2chgbddahj1ej">{stat.value}</span>
                  <span className="mt-2 block text-xs leading-relaxed text-fg-muted" data-id="element-949" data-mp="1ximdrwpi8q94">{stat.label}</span>
                </dd>
              </div>)}
          </dl>
        </Reveal>
        <div className="mt-14 grid gap-10 md:grid-cols-2 md:gap-12" data-id="element-950" data-mp="1or2ftcsoykf7">
          {palacioQuotes.map(q => <Reveal key={q.source} data-id="element-951" data-mp="1tj3zvkw3pffs">
              <figure className="border-l-2 border-coral pl-5" data-id="element-952" data-mp="1tk5fm2o2aq3u">
                <blockquote className="text-lg font-medium leading-relaxed text-fg" data-id="element-953" data-mp="1euhbyuserfcy">“{q.quote}”</blockquote>
                <figcaption className="mt-3 text-xs text-fg-faint" data-id="element-954" data-mp="1jsr8b8pvcm05">
                  — {q.source}
                  {'note' in q && q.note ? <span className="italic" data-id="element-955" data-mp="15ukrz6gyr4o8"> ({q.note})</span> : null}
                </figcaption>
              </figure>
            </Reveal>)}
        </div>
      </CaseSection>

      <CaseSection index="07" label="Reflection" title="What I'd do differently" intro={<p data-id="element-957" data-mp="37o75wj7r2zxl">
            I'd bring engineering into the co-creation workshops from the start. The white-label pivot came late because
            we learned the build constraints after the flows were set; knowing them sooner would have saved a round of
            rework.
          </p>} data-id="element-956" data-mp="3ci1nnh3sp4u1" />

      <FieldPolaroids data-id="element-958" data-mp="3qgi5o473yhrh" />

      <NextCase title="Clear Docs Mobile: deciding what loan officers don't need on a phone" href="/work/clear-docs" data-id="element-959" data-mp="3qg02sopxx5al" />
    </CaseStudyShell>;
}